import { Location, PopStateEvent } from '@angular/common';
import { Component, inject, input, isDevMode, OnDestroy, OnInit, output } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AgCellWrapperComponent, AgFloatingFilterWrapperComponent } from '@methylium/ag-grid';
import { OdataService } from '@methylium/odata';
import { AgGridModule } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ColumnState,
  ColumnVisibleEvent,
  DomLayoutType,
  FilterChangedEvent,
  FilterModel,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IDatasource,
  ModelUpdatedEvent,
  RowClassParams,
} from 'ag-grid-community';
import { Subject, SubscriptionLike } from 'rxjs';
import { filter, skip, switchMap, take } from 'rxjs/operators';
import { ListLoadingOverlayComponent } from '../list-loading-overlay/list-loading-overlay.component';

@Component({
  selector: 'ml-list-lazy',
  templateUrl: './list-lazy.component.html',
  standalone: true,
  imports: [AgGridModule],
})
export class ListLazyComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly odataService = inject(OdataService);

  readonly columnDefinitions = input.required<(ColDef | ColGroupDef)[]>();
  readonly initialFilterModel = input<FilterModel | undefined>(undefined);
  readonly initialSortModel = input<ColumnState[] | undefined>(undefined);
  readonly dataSource = input.required<IDatasource>();
  readonly rowNodeIdProperty = input('id');
  readonly getRowClass = input<((params: RowClassParams<unknown>) => string | string[] | undefined) | undefined>(
    undefined
  );
  readonly defaultToolTip = input<string | undefined>(undefined);
  readonly rowHeight = input<number | undefined>(undefined);
  readonly headerHeight = input<number | undefined>(undefined);
  readonly domLayout = input<DomLayoutType>('autoHeight');

  readonly cellClicked = output<CellClickedEvent>();
  readonly columnVisible = output<ColumnVisibleEvent>();
  readonly gridReady = output<GridReadyEvent>();
  readonly modelUpdated = output<ModelUpdatedEvent>();

  private silentUpdateFilterQueryParam = true;
  private subscriptions: SubscriptionLike[] = [];

  public gridOptions: GridOptions = {};
  public components: Record<string, unknown>;

  private gridApi: GridApi;
  private resizeHandler: EventListenerOrEventListenerObject;

  ngOnInit() {
    this.components = {
      agFloatingFilterWrapperComponent: AgFloatingFilterWrapperComponent,
      agCellWrapperComponent: AgCellWrapperComponent,
    };

    const rowNodeIdProperty = this.rowNodeIdProperty();
    this.gridOptions = {
      debug: isDevMode(),
      rowModelType: 'infinite',
      cacheOverflowSize: 0,
      maxConcurrentDatasourceRequests: 1,
      maxBlocksInCache: 5,
      cacheBlockSize: 100,
      columnDefs: this.columnDefinitions(),
      loadingOverlayComponent: 'customLoadingOverlay',
      defaultColDef: {
        resizable: true,
        sortable: true,
        tooltipValueGetter: () => this.defaultToolTip(),
      },
      components: {
        customLoadingOverlay: ListLoadingOverlayComponent,
      },
      getRowId(item) {
        return item.data[rowNodeIdProperty];
      },
    };

    const domLayout = this.domLayout();
    this.gridOptions.domLayout = domLayout === 'normal' ? domLayout : 'autoHeight';

    const locationSubject: Subject<PopStateEvent> = new Subject<PopStateEvent>();

    // Get the filter model from the query parameters and set it as the filterModel for the grid API
    this.subscriptions.push(
      locationSubject
        .pipe(
          filter((popState: PopStateEvent) => popState.pop === true), // Only act on navigating back/forward buttons.
          switchMap(() => this.route.queryParamMap.pipe(skip(1), take(1)))
        )
        .subscribe((paramMap: ParamMap) => {
          const filterModel = this.getFilterModelFromQueryParam(paramMap);

          // Prevent adding a history state after navigating forward/backward.
          this.silentUpdateFilterQueryParam = true;
          this.gridApi.setFilterModel(filterModel ?? null);
        })
    );

    this.subscriptions.push(
      this.location.subscribe((popStateEvent: PopStateEvent) => {
        locationSubject.next(popStateEvent);
      })
    );
  }

  onGridReady(readyParams: GridReadyEvent) {
    this.gridApi = readyParams.api;

    // Get the initial filter model from the query parameters and set it as the filterModel for the grid API
    this.route.queryParamMap.pipe(take(1)).subscribe((queryParamMap) => {
      const filterModelOnInit = this.getFilterModelFromQueryParam(queryParamMap);

      // Reset the silent update setting, so we can navigate back to the initial filter
      this.silentUpdateFilterQueryParam = false;

      readyParams.api.setFilterModel(filterModelOnInit ?? null);
    });

    this.gridApi.updateGridOptions({ datasource: this.dataSource() });

    this.resizeHandler = function () {
      setTimeout(() => {
        readyParams.api.sizeColumnsToFit();
      });
    };

    window.addEventListener('resize', this.resizeHandler);

    readyParams.api.sizeColumnsToFit();
    const initialSort = this.initialSortModel();
    if (initialSort) {
      readyParams.api.applyColumnState({ state: initialSort });
    }

    this.gridOptions.getRowClass = this.getRowClass();
    this.gridReady.emit(readyParams);
  }

  onFilterChanged(params: FilterChangedEvent) {
    const filterModel = params.api.getFilterModel();
    this.setFilterQueryParam(filterModel);

    // Reset the silent update setting, so we can navigate back and forward between earlier filter options.
    this.silentUpdateFilterQueryParam = false;
    this.gridApi.deselectAll();
  }

  onCellClicked(cellParams: CellClickedEvent) {
    this.cellClicked.emit(cellParams);
  }

  onModelUpdated(event: ModelUpdatedEvent) {
    this.modelUpdated.emit(event);
    this.gridApi.sizeColumnsToFit();
  }

  private getFilterModelFromQueryParam(queryParamMap: ParamMap): FilterModel | undefined {
    const filterQueryParam = queryParamMap.get('$filter');
    return filterQueryParam ? this.odataService.generateFilterModel(filterQueryParam) : this.initialFilterModel();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  private setFilterQueryParam(filterModel: FilterModel) {
    const filterParamsString = this.odataService.generateFilterStringForBrowser(filterModel);
    this.router
      .navigateByUrl(
        this.router.createUrlTree([], {
          queryParams: {
            $filter: filterParamsString,
          },
          queryParamsHandling: 'merge',
        }),
        { replaceUrl: this.silentUpdateFilterQueryParam }
      )
      .catch((error: unknown) => {
        console.error('Failed to update filter query params:', error);
      });
  }
}
